import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Select, Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockIcon from '@mui/icons-material/Lock';
import { campaignAccessAPI } from '../services/api';
import { useRules, permissionError } from '../hooks/useRules';

// Campaign ⋮ menu: assign people (Editor / Viewer), lock, transfer ownership, pick up.
// Mirrors ReachOut's position menu. Every action is also checked by the server.

const ACCESS = { EDITOR: 'Editor', VIEWER: 'Viewer' };

function AssignDialog({ campaign, onClose, onSaved }) {
  const rules = useRules();
  const [people, setPeople] = useState([]);
  const [members, setMembers] = useState(campaign.members || []);
  const [saved, setSaved] = useState(campaign.members || []);
  const [pick, setPick] = useState('');
  const [access, setAccess] = useState(rules.policy('assign.default_access') || 'EDITOR');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    campaignAccessAPI.assignableUsers().then((res) => setPeople(res.data || [])).catch(() => setPeople([]));
    campaignAccessAPI.members(campaign.id).then((res) => { setMembers(res.data || []); setSaved(res.data || []); }).catch(() => {});
  }, [campaign.id]);

  const taken = new Set(members.map((m) => m.email));
  const choices = people.filter((p) => !taken.has(p.email));

  const add = () => {
    const person = people.find((p) => p.email === pick);
    if (!person) return;
    setMembers((ms) => [...ms, { email: person.email, name: person.name, access }]);
    setPick('');
  };

  // A person chosen in the picker but not yet "Add"-ed is saved too.
  const pending = people.find((p) => p.email === pick && !taken.has(p.email));
  const finalList = pending ? [...members, { email: pending.email, name: pending.name, access }] : members;
  const key = (list) => list.map((m) => `${m.email}:${m.access}`).sort().join('|');
  const dirty = key(finalList) !== key(saved);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await campaignAccessAPI.setMembers(campaign.id, finalList.map((m) => ({ email: m.email, access: m.access })));
      onSaved();
    } catch (err) {
      setError(permissionError(err, 'Could not save the assignments.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" onClick={(e) => e.stopPropagation()}>
      <DialogTitle>Assign people · {campaign.name}</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0 }}>
          Editors can work on this campaign (following their role&apos;s rules). Viewers can only look.
        </p>
        {members.length === 0 && <div style={{ fontSize: 13, color: '#6B7280', padding: '8px 0' }}>Nobody is assigned yet.</div>}
        {members.map((m) => (
          <div key={m.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ flex: 1, fontSize: 13 }}>
              <div style={{ fontWeight: 600 }}>{m.name || m.email}</div>
              <div style={{ color: '#6B7280', fontSize: 12 }}>{m.email}</div>
            </div>
            <Select size="small" value={m.access}
              onChange={(e) => setMembers((ms) => ms.map((x) => (x.email === m.email ? { ...x, access: e.target.value } : x)))}>
              {Object.entries(ACCESS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </Select>
            <Button size="small" color="error" onClick={() => setMembers((ms) => ms.filter((x) => x.email !== m.email))}>Remove</Button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
          <Select size="small" displayEmpty value={pick} onChange={(e) => setPick(e.target.value)} style={{ flex: 1 }}>
            <MenuItem value=""><em>Choose a person…</em></MenuItem>
            {choices.map((p) => <MenuItem key={p.email} value={p.email}>{p.name} ({p.email})</MenuItem>)}
          </Select>
          <Select size="small" value={access} onChange={(e) => setAccess(e.target.value)}>
            {Object.entries(ACCESS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </Select>
          <Button variant="outlined" size="small" disabled={!pick} onClick={add}>Add</Button>
        </div>
        {error && <div role="alert" style={{ color: '#DC2626', fontSize: 12, marginTop: 10 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function TransferDialog({ campaign, onClose, onSaved }) {
  const [people, setPeople] = useState([]);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    campaignAccessAPI.assignableUsers().then((res) => setPeople(res.data || [])).catch(() => setPeople([]));
  }, []);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await campaignAccessAPI.transfer(campaign.id, email);
      onSaved();
    } catch (err) {
      setError(permissionError(err, 'Could not transfer the campaign.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" onClick={(e) => e.stopPropagation()}>
      <DialogTitle>Transfer ownership</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0 }}>The new owner gets full owner access to “{campaign.name}”.</p>
        <Select size="small" fullWidth displayEmpty value={email} onChange={(e) => setEmail(e.target.value)}>
          <MenuItem value=""><em>Choose the new owner…</em></MenuItem>
          {people.map((p) => <MenuItem key={p.email} value={p.email}>{p.name} ({p.email})</MenuItem>)}
        </Select>
        {error && <div role="alert" style={{ color: '#DC2626', fontSize: 12, marginTop: 10 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !email}>Transfer</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Small lock badge for cards and headers. */
export function LockedBadge({ campaign }) {
  if (!campaign?.locked) return null;
  return (
    <Tooltip title={`Locked${campaign.locked_by ? ` by ${campaign.locked_by}` : ''} — only managers and admins can change it`}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, padding: '2px 7px',
                     borderRadius: 4, background: '#FEF3C7', color: '#92400E' }}>
        <LockIcon style={{ fontSize: 11 }} /> Locked
      </span>
    </Tooltip>
  );
}

export default function CampaignAccessMenu({ campaign, onChanged }) {
  const rules = useRules();
  const [anchor, setAnchor] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState('');

  const can = (key) => rules.canOn(campaign, key);
  const canPickUp = rules.policyOn('campaigns.open_pool') && rules.hasRole('RECRUITER')
    && !campaign.my_access && (campaign.members || []).length === 0;
  const items = [
    can('campaign.assign') && ['assign', 'Assign people…'],
    can('campaign.lock') && ['lock', campaign.locked ? 'Unlock' : 'Lock'],
    can('campaign.transfer') && ['transfer', 'Transfer ownership…'],
    canPickUp && ['pickup', 'Pick up this campaign'],
  ].filter(Boolean);

  if (items.length === 0) return null;

  const done = () => { setDialog(null); setError(''); onChanged?.(); };

  const run = async (id) => {
    setAnchor(null);
    setError('');
    try {
      if (id === 'lock') {
        await campaignAccessAPI.setLocked(campaign.id, !campaign.locked);
        done();
      } else if (id === 'pickup') {
        await campaignAccessAPI.pickUp(campaign.id);
        done();
      } else {
        setDialog(id);
      }
    } catch (err) {
      const text = permissionError(err, 'That change could not be made.');
      setError(text);
      window.alert(text);
    }
  };

  return (
    <span onClick={(e) => e.stopPropagation()} title={error || undefined}>
      <IconButton size="small" aria-label="Campaign access" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {items.map(([id, label]) => <MenuItem key={id} onClick={() => run(id)}>{label}</MenuItem>)}
      </Menu>
      {dialog === 'assign' && <AssignDialog campaign={campaign} onClose={() => setDialog(null)} onSaved={done} />}
      {dialog === 'transfer' && <TransferDialog campaign={campaign} onClose={() => setDialog(null)} onSaved={done} />}
    </span>
  );
}
